import { useEffect, useState, useRef } from "react";
import { Send, Upload, FileText, Download, Play, Trash2 } from "lucide-react";
import api, { API } from "../lib/api";
import { toast } from "sonner";
import { formatDistanceToNow, format } from "date-fns";

export const STAGES = ["Planning", "Filming", "Editing", "Review", "Complete"];

export function ProgressStepper({ stage }) {
  const MAP = { Filming: 1, Editing: 2, "In revision": 3, Review: 3, Completed: 4, Complete: 4 };
  const idx = MAP[stage] ?? 0;
  return (
    <div className="flex items-center" data-testid="progress-stepper">
      {STAGES.map((s, i) => (
        <div key={s} className="flex items-center flex-1 last:flex-none">
          <div className="flex flex-col items-center gap-1.5">
            <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${i <= idx ? "bg-brand-orange text-white" : "bg-brand-purple/10 text-brand-purple/40"}`}>
              {i + 1}
            </div>
            <span className={`text-[11px] font-medium whitespace-nowrap ${i <= idx ? "text-brand-purpleDeep" : "text-brand-purpleDeep/40"}`}>{s}</span>
          </div>
          {i < STAGES.length - 1 && <div className={`h-0.5 flex-1 -mt-5 mx-1 ${i < idx ? "bg-brand-orange" : "bg-brand-purple/15"}`} />}
        </div>
      ))}
    </div>
  );
}

export function Timeline({ projectId }) {
  const [acts, setActs] = useState([]);
  useEffect(() => { api.get(`/projects/${projectId}/activities`).then(({ data }) => setActs(data)); }, [projectId]);
  if (!acts.length) return <p className="text-sm text-brand-purpleDeep/40">No activity yet.</p>;
  return (
    <div className="relative pl-6" data-testid="project-timeline">
      <div className="absolute left-[7px] top-1 bottom-1 w-px bg-brand-purple/15" />
      {acts.map((a) => (
        <div key={a.id} className="relative pb-5 last:pb-0">
          <div className="absolute -left-[22px] top-0.5 w-3.5 h-3.5 rounded-full bg-brand-orange border-2 border-white" />
          <div className="text-sm font-medium text-brand-purpleDeep">{a.description}</div>
          <div className="text-xs text-brand-purpleDeep/40">{a.created_at ? format(new Date(a.created_at), "d MMM yyyy, h:mm a") : ""}</div>
        </div>
      ))}
    </div>
  );
}

export function MessageThread({ projectId, me }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const boxRef = useRef();
  const load = () => api.get(`/projects/${projectId}/messages`).then(({ data }) => setMsgs(data));
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [projectId]);
  useEffect(() => { boxRef.current?.scrollTo(0, boxRef.current.scrollHeight); }, [msgs]);

  const send = async () => {
    if (!text.trim()) return;
    await api.post(`/projects/${projectId}/messages`, { body: text });
    setText(""); load();
  };

  return (
    <div className="flex flex-col h-[420px]" data-testid="message-thread">
      <div ref={boxRef} className="flex-1 overflow-y-auto space-y-3 pr-1">
        {msgs.length === 0 && <p className="text-sm text-brand-purpleDeep/40 text-center pt-8">No messages yet. Say hello!</p>}
        {msgs.map((m) => {
          const mine = m.sender_role === (me?.role);
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${mine ? "bg-brand-purple text-white" : "bg-[#F3F0F5] text-brand-purpleDeep"}`}>
                <div className="text-[11px] opacity-70 mb-0.5">{m.sender_name}</div>
                <div className="text-sm">{m.body}</div>
                <div className="text-[10px] opacity-50 mt-1">{m.created_at ? formatDistanceToNow(new Date(m.created_at), { addSuffix: true }) : ""}</div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type a message…" className="flex-1 bg-[#F3F0F5] rounded-lg px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brand-purple/20" data-testid="message-input" />
        <button onClick={send} className="bg-brand-orange hover:bg-brand-orangeDark text-white px-4 rounded-lg" data-testid="message-send"><Send size={17} /></button>
      </div>
    </div>
  );
}

export function FileList({ projectId, canUpload, canDelete }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const load = () => api.get(`/projects/${projectId}/files`).then(({ data }) => setFiles(data));
  useEffect(() => { load(); }, [projectId]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file); fd.append("project_id", projectId); fd.append("category", "document");
    try {
      await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File uploaded"); load();
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const download = (f) => {
    const token = localStorage.getItem("ev_token");
    window.open(`${API}/files/${f.id}/download?auth=${token}`, "_blank");
  };
  const remove = async (id) => { await api.delete(`/files/${id}`); load(); };

  return (
    <div data-testid="file-list">
      {canUpload && (
        <label className="flex items-center gap-3 p-4 mb-4 rounded-xl border border-dashed border-border cursor-pointer hover:border-brand-purple/40 transition-colors" data-testid="file-upload-label">
          <Upload size={18} className="text-brand-purple" />
          <span className="text-sm text-brand-purpleDeep/70">{uploading ? "Uploading…" : "Upload a file (script, storyboard, footage, docs)"}</span>
          <input type="file" className="hidden" onChange={upload} disabled={uploading} />
        </label>
      )}
      <div className="space-y-2">
        {files.length === 0 && <p className="text-sm text-brand-purpleDeep/40">No files yet.</p>}
        {files.map((f) => (
          <div key={f.id} className="flex items-center gap-3 p-3 rounded-lg border border-border bg-white" data-testid={`file-${f.id}`}>
            <FileText size={18} className="text-brand-purple shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-brand-purpleDeep truncate">{f.original_filename}</div>
              <div className="text-xs text-brand-purpleDeep/40">{f.uploaded_by_name} · {(f.size / 1024).toFixed(0)} KB</div>
            </div>
            <button onClick={() => download(f)} className="text-brand-purpleDeep/40 hover:text-brand-purple"><Download size={16} /></button>
            {canDelete && <button onClick={() => remove(f.id)} className="text-brand-purpleDeep/40 hover:text-red-500"><Trash2 size={16} /></button>}
          </div>
        ))}
      </div>
    </div>
  );
}

export function VideoPlayer({ url }) {
  if (!url) return (
    <div className="aspect-video rounded-xl bg-brand-purpleDeep/5 border border-border flex items-center justify-center text-brand-purpleDeep/40 text-sm">
      No video uploaded yet
    </div>
  );
  const isEmbed = url.includes("youtube") || url.includes("vimeo");
  return (
    <div className="aspect-video rounded-xl overflow-hidden bg-black" data-testid="video-player">
      {isEmbed ? <iframe src={url} className="w-full h-full" allow="fullscreen" title="Video" /> : <video src={url} controls className="w-full h-full" />}
    </div>
  );
}
